import { useRef, useState, useMemo, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import './App.css';

const Crowd = ({ numOfPeople }: { numOfPeople: number }) => {
  const parentRef = useRef<HTMLDivElement | null>(null);

  const PERSON_HEIGHT = 160; // fixed height for all images
  const PERSON_GAP = 8; // horizontal spacing between images
  const ROW_VERTICAL_OVERLAP = 60; // pixels to overlap rows
  const ROW_ESTIMATE = PERSON_HEIGHT - ROW_VERTICAL_OVERLAP; // virtualizer estimate

  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    const el = parentRef.current;
    if (!el) return;

    setContainerWidth(el.offsetWidth);

    const ro = new ResizeObserver(() => {
      setContainerWidth(el.offsetWidth);
    });
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  const peopleImages = useMemo(
    () =>
      Array.from({ length: numOfPeople }, () =>
        Math.random() < 0.5 ? '/person.png' : '/person2.png'
      ),
    [numOfPeople]
  );

  const AVG_PERSON_WIDTH = 60;
  const peoplePerRow = useMemo(() => {
    if (!containerWidth) return 10;
    return Math.max(
      1,
      Math.floor(containerWidth / (AVG_PERSON_WIDTH + PERSON_GAP))
    );
  }, [containerWidth]);

  const rows = useMemo(() => {
    const result: number[][] = [];
    let idx = 0;
    let toggle = true; // used to alternate

    while (idx < numOfPeople) {
      const count = toggle ? peoplePerRow : Math.max(1, peoplePerRow - 1);
      const end = Math.min(idx + count, numOfPeople);
      // eslint-disable-next-line no-loop-func
      result.push(Array.from({ length: end - idx }, (_, i) => idx + i));
      idx = end;
      toggle = !toggle;
    }

    return result;
  }, [numOfPeople, peoplePerRow]);

  // virtualizer
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_ESTIMATE,
    overscan: 5,
  });

  useEffect(() => {
    rowVirtualizer.measure();
  }, [rows, containerWidth, rowVirtualizer]);

  if (numOfPeople === 0) return <div className="crowd" ref={parentRef} />;

  return (
    <div className="crowd" ref={parentRef}>
      <div
        className="crowd-inner"
        style={{ height: `${rowVirtualizer.getTotalSize()}px` }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const rowPeople = rows[virtualRow.index];
          return (
            <div
              key={virtualRow.key}
              className="crowd-row"
              style={{
                height: PERSON_HEIGHT,
                transform: `translateY(${virtualRow.start}px)`,
                gap: PERSON_GAP,
              }}
            >
              {rowPeople.map((idx) => (
                <div
                  key={idx}
                  className="person"
                  style={{
                    height: PERSON_HEIGHT,
                  }}
                >
                  <img src={peopleImages[idx]} draggable={false} alt="person" />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CrowdControl = ({
  numOfPeople,
  setNumOfPeople,
}: {
  numOfPeople: number;
  setNumOfPeople: React.Dispatch<React.SetStateAction<number>>;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onDecrease = () => setNumOfPeople((v) => Math.max(0, v - 1));
  const onIncrease = () => setNumOfPeople((v) => v + 1);

  return (
    <div className="input-wrapper">
      <button className={numOfPeople === 0 ? 'hide' : ''} onClick={onDecrease}>
        -
      </button>
      <input
        ref={inputRef}
        type="number"
        min={0}
        max={80085}
        value={String(numOfPeople)}
        // onSelect={() => inputRef.current?.select()}
        onChange={(e) => {
          const val = e.target.value;
          if (val.includes('.')) return;

          const number = val === '' ? 0 : Number(val);
          setNumOfPeople(number);
        }}
      />
      <button onClick={onIncrease}>+</button>
    </div>
  );
};

export default function App() {
  const [numOfPeople, setNumOfPeople] = useState(0);

  return (
    <div className="page-wrapper">
      <header>
        <h1>i'm going to get stage fright</h1>
        <div className="options">
          <div className="input-wrapper">
            <p># of people: </p>
            <CrowdControl
              numOfPeople={numOfPeople}
              setNumOfPeople={setNumOfPeople}
            />
          </div>
        </div>
      </header>

      <Crowd numOfPeople={numOfPeople} />

      <div className="me-wrapper">
        <div className="me">
          <p>me</p>
        </div>
      </div>
    </div>
  );
}
